import { create } from 'zustand';

/**
 * The API refuses requests from Tor exit nodes with a 403 `TOR_BLOCKED` (#118
 * R34). Once any response says so, the whole SPA is unusable, so the root gate
 * in `main.tsx` swaps the router for the block page. This module is the shared
 * signal between the places a response can arrive and that gate.
 */
export const TOR_BLOCKED_CODE = 'TOR_BLOCKED';

interface TorBlockState {
  blocked: boolean;
}

export const useTorBlockStore = create<TorBlockState>(() => ({ blocked: false }));

/** Record that the API refused this client as a Tor user. Never cleared: a reload starts over. */
export function markTorBlocked(): void {
  if (!useTorBlockStore.getState().blocked) useTorBlockStore.setState({ blocked: true });
}

/**
 * True for a 403 whose error envelope carries `TOR_BLOCKED`. The body is read
 * from a clone, so the caller can still read the response itself.
 */
export async function isTorBlockedResponse(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  try {
    const body = (await res.clone().json()) as { error?: { code?: unknown } } | null;
    return body?.error?.code === TOR_BLOCKED_CODE;
  } catch {
    return false;
  }
}
